import { Content } from '../../types/tour';

export const content: Content[] = [
  {
    icon: 'video-audio',
    title: 'Try the Video Calling API',
    paragraphs: [
      '<strong>In this interactive tutorial, we will cover the core concepts of the Video Calling API</strong>. If you have not looked at our React Video Components yet, this tutorial will give you a guided tour of our components and allow you to try them in real-time.',
    ],
  },
  {
    icon: 'video-audio',
    title: 'Setup Client',
    paragraphs: [
      'At the end of this tutorial, our goal is to have a working meetings application where our user is able to join and other users can join via Stream’s demo application.',
      "To get started, let's configure our client and user for our application.",
      "Like our Chat <a href='https://getstream.io/chat/'>API</a>, the Video <a href='https://getstream.io/video/'>API</a> requires an API key and User token before the client is able to connect.",
    ],
    blockquotes: [],
  },
  {
    icon: 'video-audio',
    title: 'Create Call',
    paragraphs: [
      'With the client and user configured, we can move on to creating a call.',
      'Creating calls can be doing using the client object we created in the previous section. The first argument supplied to the call is the Call Type.',
      'Different calls and use-cases can have different call types and permissions. For example, if your application centers around live content, <code>livestream</code> may be a more appropriate call type. Similar for audio only applications, <code>audio_room</code> is better optimized for audio only use-cases.',
      'The second argument in our function is the Call ID. This represents the unique identifier of our call and can be shared with other participants, allowing them to join.',
      'If left blank, an ID is provided for you.',
    ],
    blockquotes: [
      'Note: You can call getOrCreate to create the call but not join it. For this guide, we will join the call directly.',
    ],
  },
  {
    icon: 'video-audio',
    title: 'Join Call',
    paragraphs: [
      'With our call created, we can join by calling the join method on our call object. Using Stream’s UI components, we can have a fully featured Meetings interface in just a few lines of code.',
      'At the top of our component tree, we are passing in the client object which was just initialized along with the call we created.',
    ],
    blockquotes: [
      'Note: You can call getOrCreate to create the call but not join it. For this guide, we will join the call directly.',
    ],
  },
  {
    icon: 'video-audio',
    title: 'Customizing our UI',
    paragraphs: [
      'Stream’s components are designed to be flexible and customizable allowing developers to quickly build prototypes and customize the UI to fit their application’s needs and design language.',
      'Depending on your needs you can:',
      '<li>Build from the low level client that you’ve just learned to use</li>',
      '<li>Use our UI components</li>',
      '<li>Mix and match between your own UI and our components</li>',

      'As an example let’s consider the code below, by using props, we can configure what options appear to the end user or allow them to pass in custom call controls.',
    ],
    blockquotes: [],
  },
  {
    icon: 'video-audio',
    title: 'Observing the Call State',
    paragraphs: [
      'During the call, you may want to update different parts of our application UI based on different factors such as whether the current participant has their microphone muted, a user requests permission to speak or screen share or another event triggered during a call. ',
      'To facilitate this, we can use predefined hooks to access different parts of the running application such as the participants, call state, etc. ',
      'For a detailed look at the different hooks available for use, we recommend looking at our React SDK documentation. ',
      'By using the <code>CallState</code> and other properties, it is possible to access and manipulate different parts of a call which can in turn be used to develop custom behaviour and UI.',
    ],
    blockquotes: [],
  },
  {
    icon: 'video-audio',
    title: 'Exploring other use-cases',
    paragraphs: [
      "In this example, we primarily looked at a meeting style application but using the API, it is possible to create applications ranging from <a href='https://getstream.io/video/livestreaming/'>Livestreaming</a>, <a href='https://getstream.io/video/audio-rooms/'>Audio Room</a> and even <a href='https://getstream.io/video/voice-calling/'>WhatsApp</a> style calling.",
      'Out of the box, developer can use one of our predefined Call Type or create an entirely new CallType and permission system tailored to their application.',
      'For a detailed look at the different hooks available for use, we recommend looking at our React SDK documentation. ',
      "Common protocols like <a href='https://getstream.io/glossary/#real-time-messaging-protocol'>RTMP</a>, <a href='https://getstream.io/glossary/#hls-protocol'>HLS</a>, Opus Red and Opus DTX are all available from day one — backed by Stream’s robust <a href='https://getstream.io/blog/chat-edge-infrastructure/'>Global Edge Network</a>.",
    ],
    blockquotes: [],
  },
  {
    icon: 'video-audio',
    title: 'Conclusion',
    paragraphs: [],
  },
];
